import type {
  ConversationDetail,
  ConversationSummary,
  FileInfo,
  Invitation,
  Me,
  Message,
  Person,
  ScheduledMessage,
  Seq,
  ServerInfo,
} from '@sd/contracts';
import { createStore, type StoreApi } from 'zustand/vanilla';

/** Socket/sync state shown in the title-bar pill and the chat banner. */
export type ConnectionState = 'connecting' | 'online' | 'offline' | 'syncing';

/** The five send states of the plan: 대기 / 전송 중 / 서버 저장 완료 / 읽음 / 실패. */
export type SendState = 'queued' | 'sending' | 'sent' | 'read' | 'failed';

/** An own message the server has not confirmed yet. Keyed by its idempotency key. */
export interface PendingMessage {
  clientMessageId: string;
  conversationId: string;
  text: string;
  replyToMessageId: string | null;
  attachmentIds: string[];
  /** Attachment metadata for rendering while pending. */
  attachments: FileInfo[];
  mentions: string[];
  createdAt: string;
  attempts: number;
  state: 'queued' | 'sending' | 'failed';
  /** Contract error code or "NETWORK" of the last failure. */
  lastError: string | null;
  /** Upload still running for this message (rendered as the in-bubble upload card). */
  upload: { name: string; ext: string; size: number; sentBytes: number } | null;
}

export interface Timeline {
  conversationId: string;
  /** Server-confirmed messages, ascending by seq, unique by id. */
  messages: Message[];
  hasMoreBefore: boolean;
  hasMoreAfter: boolean;
  historyStartSeq: Seq;
  /** Event cursor (per-conversation eventSeq) already applied to this timeline. */
  eventCursor: Seq;
  loading: boolean;
  loaded: boolean;
  /** First unread seq when the conversation was opened ("여기서부터 안 읽은 메시지"). */
  unreadAnchorSeq: Seq | null;
}

export type TrayItemState = 'uploading' | 'scanning' | 'ready' | 'unscannable' | 'infected' | 'failed';

/** A file waiting in the composer tray (upload → scan → send). */
export interface TrayItem {
  localId: string;
  conversationId: string;
  name: string;
  ext: string;
  size: number;
  state: TrayItemState;
  /** 0–100 while uploading. */
  progress: number;
  fileId: string | null;
  file: FileInfo | null;
  error: string | null;
  /** "파일 보내기" was pressed while it was still uploading or being scanned: it goes once the scan says it is clean. */
  sendWhenReady?: boolean;
}

export type DownloadState =
  | { state: 'busy'; downloadId: string | null; receivedBytes: number; totalBytes: number | null }
  | { state: 'done'; downloadId: string }
  | { state: 'browser' }
  | { state: 'failed'; reason: string };

export type ClientStatus = 'idle' | 'starting' | 'ready' | 'signed_out' | 'error';

export interface ClientState {
  status: ClientStatus;
  /** Contract error code / "NETWORK" when `status === 'error'`. */
  error: string | null;
  serverId: string;
  serverInfo: ServerInfo | null;
  me: Me | null;
  connection: ConnectionState;
  people: Record<string, Person>;
  online: Record<string, boolean>;
  /** "입력 중": conversation → person → until when (epoch ms). Only other people, only while connected. */
  typing: Record<string, Record<string, number>>;
  /** "저장한 메시지" (`messages.saved`): the ids the viewer saved, kept current by `message.saved` user events. */
  saved: Record<string, true>;
  /** "예약 전송" (`messages.scheduled`): the viewer's messages still waiting, by id, kept current by user events. */
  scheduled: Record<string, ScheduledMessage>;
  conversations: Record<string, ConversationSummary>;
  details: Record<string, ConversationDetail>;
  timelines: Record<string, Timeline>;
  pending: Record<string, PendingMessage>;
  /** Own reaction changes the server has not answered yet: message id → emoji → whether the viewer wants it on. */
  pendingReactions: Record<string, Record<string, boolean>>;
  drafts: Record<string, string>;
  trays: Record<string, TrayItem[]>;
  downloads: Record<string, DownloadState>;
  conversationFiles: Record<string, FileInfo[]>;
  invitations: Invitation[];
  activeConversationId: string | null;
  /** Do-not-disturb end (ISO) or null: app-wide on desktop (main's setting), this tab only on the web. */
  dndUntil: string | null;
  /** Where pending sends and drafts live: OS-encrypted on desktop, memory on the web. */
  storage: 'persistent' | 'memory' | 'unavailable';
  /** Bumps when a message should be highlighted after a jump (search → original). */
  flash: { messageId: string; nonce: number } | null;
  /**
   * A conversation asked for from outside the screen (a clicked notification, the address the app was opened
   * with): the screen brings it to the front, on phones too, then clears it (MessengerClient.showConversation).
   */
  shown: { conversationId: string; nonce: number } | null;
}

export type ClientStore = StoreApi<ClientState>;

export function initialClientState(serverId: string): ClientState {
  return {
    status: 'idle',
    error: null,
    serverId,
    serverInfo: null,
    me: null,
    connection: 'connecting',
    people: {},
    online: {},
    typing: {},
    saved: {},
    scheduled: {},
    conversations: {},
    details: {},
    timelines: {},
    pending: {},
    pendingReactions: {},
    drafts: {},
    trays: {},
    downloads: {},
    conversationFiles: {},
    invitations: [],
    activeConversationId: null,
    dndUntil: null,
    storage: 'memory',
    flash: null,
    shown: null,
  };
}

export function isDndOn(state: Pick<ClientState, 'dndUntil'>, now: Date): boolean {
  return Boolean(state.dndUntil && new Date(state.dndUntil).getTime() > now.getTime());
}

export function createClientStore(serverId: string): ClientStore {
  return createStore<ClientState>()(() => initialClientState(serverId));
}
